import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../config/firebase';
// Search functionality moved to local state
import { useUserRoles } from '../../contexts/UserRolesContext';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSystemPreset } from '../../contexts/SystemPresetContext';
import { getCurrentMillis } from '../../utils/dateUtils';
import type { Client, Role } from '../../types';
import { Users as UsersIcon, RefreshCw, Download, Phone, MapPin, Mail, Calendar, User, ChevronRight, X, Building2, Plus, Search, ArrowRight, ChevronDown, Save } from 'lucide-react';
import EditButton from '../../components/ui/EditButton';
import { SectionHeader } from '../../components/ui';
import * as XLSX from 'xlsx';

const MyClients: React.FC = () => {
  const { roles, loading: rolesLoading } = useUserRoles();
  // Search functionality moved to local state
  const { strings, language, isRTL } = useLocalization();
  const { user } = useAuth();
  const { getLawyerTitleName } = useSystemPreset();
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [searchQuery, setSearchQuery] = useState(''); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // New client functionality
  const [showAddClient, setShowAddClient] = useState(false);
  const [phoneSearch, setPhoneSearch] = useState('');
  const [searchingClient, setSearchingClient] = useState(false);
  const [foundClient, setFoundClient] = useState<Client | null>(null);
  const [showClientForm, setShowClientForm] = useState(false);
  const [addingClient, setAddingClient] = useState(false);
  const [clientAlreadyAssociated, setClientAlreadyAssociated] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClient, setEditingClient] = useState(false);
  const [newClientForm, setNewClientForm] = useState({
    name: '',
    email: '',
    nationalID: '',
    birthDate: '',
    mobileNumber: '',
    mobileNumber2: '',
    address: '',
    maritalStatus: '',
    gender: '',
    photoURL: ''
  });
  const [editClientForm, setEditClientForm] = useState({
    name: '',
    email: '',
    nationalID: '',
    birthDate: '',
    mobileNumber: '',
    mobileNumber2: '',
    address: '',
    maritalStatus: '',
    gender: '',
    photoURL: ''
  });

  // Debug logging
  console.log('🔍 MyClients render - roles:', roles.length, 'selectedRole:', selectedRole?.id, 'rolesLoading:', rolesLoading);

  // Filter clients based on search query
  const filteredClients = clients.filter(client => {
    const searchQueryLower = searchQuery.toLowerCase();
    const matchesSearch = client.name.toLowerCase().includes(searchQueryLower) ||
                         (client.email || '').toLowerCase().includes(searchQueryLower) ||
                         (client.mobileNumber || '').toLowerCase().includes(searchQueryLower) ||
                         client.id.toLowerCase().includes(searchQueryLower);
    
    return matchesSearch;
  });

  const fetchClientsForRole = useCallback(async (role: Role, isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      console.log('🔄 Fetching clients for role:', role.id, 'Office:', role.officeName);

      // Query clients collection where rolesIDs array contains the selected role ID
      const clientsRef = collection(db, 'Clients');
      const q = query(
        clientsRef,
        where('rolesIDs', 'array-contains', role.id)
      );

      const querySnapshot = await getDocs(q);
      const fetchedClients: Client[] = [];

      querySnapshot.forEach((doc) => {
        const clientData = doc.data();
        const client: Client = {
          id: doc.id,
          ...clientData,
          createdAt: clientData.createdAt || '',
          updatedAt: clientData.updatedAt || '',
          createdBy: clientData.createdBy || '',
          updatedBy: clientData.updatedBy || '',
        } as Client;
        fetchedClients.push(client);
        console.log('✅ Client loaded:', client.name);
      });

      console.log('✅ Successfully fetched clients:', fetchedClients.length);
      setClients(fetchedClients);
    } catch (error) {
      console.error('❌ Error fetching clients for role:', error);
      setError('Failed to load clients. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Set first role as default when roles are loaded
  useEffect(() => {
    if (roles.length > 0 && !selectedRole) {
      console.log('🎯 Setting first role as default:', roles[0]);
      setSelectedRole(roles[0]);
    }
  }, [roles, selectedRole]);

  // Fetch clients when a role is selected
  useEffect(() => {
    if (selectedRole) {
      console.log('🔄 Fetching clients for selected role:', selectedRole);
      fetchClientsForRole(selectedRole);
    }
  }, [selectedRole, fetchClientsForRole]);

  // No need for fetchClientDetails since we already have all client data

  const handleRoleChange = (role: Role) => {
    setSelectedRole(role);
    setSelectedClient(null);
    fetchClientsForRole(role);
  };

  const handleClientSelect = (client: Client) => {
    console.log('👆 Client selected:', client.name);
    setSelectedClient(client);
  };

  const handleRefresh = () => {
    if (selectedRole) {
      fetchClientsForRole(selectedRole, true);
    }
  };

  // Validate if user has offices before adding client
  const handleAddClientClick = () => {
    if (roles.length === 0) {
      const message = language === 'ar' 
        ? 'يجب عليك إضافة مكتب جديد أو الحصول على صلاحية كعضو فريق من مستخدم آخر أولاً'
        : 'You must add a new office or have access as a team member from another user first';
      alert(message);
      return;
    }
    setShowAddClient(true);
  };

  const exportToExcel = () => {
    try {
      const exportData = filteredClients.map(client => ({
        'Client ID': client.id,
        'Name': client.name,
        'Email': client.email || 'N/A',
        'Mobile Number': client.mobileNumber || 'N/A',
        'National ID': client.nationalID || 'N/A',
        'Address': client.address || 'N/A',
        'Gender': client.gender === '0' ? 'Male' : client.gender === '1' ? 'Female' : 'N/A',
        'Marital Status': client.maritalStatus === '0' ? 'Single' : client.maritalStatus === '1' ? 'Married' : client.maritalStatus === '2' ? 'Divorced' : client.maritalStatus === '3' ? 'Widow/Widower' : 'N/A',
        'Created At': new Date(client.createdAt).toLocaleString(),
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, 'MyClients');
      XLSX.writeFile(wb, `my_clients_export_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('Error exporting clients to Excel:', err);
      setError('Failed to export clients to Excel.');
    }
  };

  // Search client by phone number
  const searchClientByPhone = async (phoneNumber: string) => {
    if (!phoneNumber.trim() || !selectedRole) return;

    setSearchingClient(true);
    setFoundClient(null);
    setError(null);
    setClientAlreadyAssociated(false);

    try {
      const clientsRef = collection(db, 'Clients');
      const q = query(
        clientsRef,
        where('mobileNumber', '==', phoneNumber.trim())
      );

      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const clientDoc = querySnapshot.docs[0];
        const clientData = clientDoc.data();
        const foundClient: Client = {
          id: clientDoc.id,
          ...clientData
        } as Client;
        
        // Check if client is already associated with this office
        if (foundClient.rolesIDs && foundClient.rolesIDs.includes(selectedRole.id)) {
          console.log('❌ Client already associated with this office:', foundClient.name);
          setFoundClient(null);
          setClientAlreadyAssociated(true);
        } else {
          setFoundClient(foundClient);
          setClientAlreadyAssociated(false);
          console.log('✅ Client found and available for association:', foundClient.name);
        }
      } else {
        console.log('❌ No client found with phone number:', phoneNumber);
        setFoundClient(null);
      }
    } catch (error) {
      console.error('❌ Error searching client:', error);
      setError('Failed to search for client');
    } finally {
      setSearchingClient(false);
    }
  };

  // Add existing client to office
  const addExistingClientToOffice = async (client: Client) => {
    if (!selectedRole || !user) return;

    setAddingClient(true);
    try {
      const clientRef = doc(db, 'Clients', client.id);
      const updatedRolesIDs = [...(client.rolesIDs || [])];
      
      // Check if role is already in the array
      if (!updatedRolesIDs.includes(selectedRole.id)) {
        updatedRolesIDs.push(selectedRole.id);
        
        await updateDoc(clientRef, {
          rolesIDs: updatedRolesIDs,
          updatedAt: getCurrentMillis(),
          updatedBy: user.id
        });

        console.log('✅ Client added to office:', client.name);
        
        // Refresh the clients list
        await fetchClientsForRole(selectedRole);
        
        // Reset states
        setShowAddClient(false);
        setPhoneSearch('');
        setFoundClient(null);
        setShowClientForm(false);
        setClientAlreadyAssociated(false);
      } else {
        setError('Client is already associated with this office');
      }
    } catch (error) {
      console.error('❌ Error adding client to office:', error);
      setError('Failed to add client to office');
    } finally {
      setAddingClient(false);
    }
  };

  // Create new client
  const createNewClient = async () => {
    if (!selectedRole || !user) return;

    // Validate required fields
    if (!newClientForm.name.trim()) {
      setError('الاسم مطلوب');
      return;
    }
    if (!newClientForm.mobileNumber.trim()) {
      setError('رقم الهاتف المحمول مطلوب');
      return;
    }

    setAddingClient(true);
    setError(null);
    try {
      const currentTime = getCurrentMillis();
      // Format birth date to DD/MM/YYYY format
      const formatBirthDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };

      const clientData = {
        name: newClientForm.name.trim(),
        email: newClientForm.email.trim(),
        nationalID: newClientForm.nationalID.trim(),
        birthDate: formatBirthDate(newClientForm.birthDate),
        mobileNumber: newClientForm.mobileNumber.trim(),
        mobileNumber2: newClientForm.mobileNumber2.trim(),
        address: newClientForm.address.trim(),
        maritalStatus: newClientForm.maritalStatus,
        gender: newClientForm.gender,
        photoURL: newClientForm.photoURL.trim(),
        rolesIDs: [selectedRole.id],
        isArchived: false,
        isDeleted: false,
        createdAt: currentTime,
        createdBy: user.id,
        updatedAt: currentTime,
        updatedBy: user.id
      };

      const docRef = await addDoc(collection(db, 'Clients'), clientData);
      console.log('✅ New client created:', docRef.id);

      // Refresh the clients list
      await fetchClientsForRole(selectedRole);

      // Reset states
      setShowAddClient(false);
      setPhoneSearch('');
      setFoundClient(null);
      setShowClientForm(false);
      setClientAlreadyAssociated(false);
      setNewClientForm({
        name: '',
        email: '',
        nationalID: '',
        birthDate: '',
        mobileNumber: '',
        mobileNumber2: '',
        address: '',
        maritalStatus: '',
        gender: '',
        photoURL: ''
      });
    } catch (error) {
      console.error('❌ Error creating new client:', error);
      setError('Failed to create new client');
    } finally {
      setAddingClient(false);
    }
  };

  // Handle phone search
  const handlePhoneSearch = () => {
    if (phoneSearch.trim()) {
      setHasSearched(true);
      searchClientByPhone(phoneSearch);
    }
  };

  // Handle form input changes
  const handleFormChange = (field: string, value: string) => {
    setNewClientForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle edit form input changes
  const handleEditFormChange = (field: string, value: string) => {
    setEditClientForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Open edit modal and populate form
  const openEditModal = (client: Client) => {
    // Convert DD/MM/YYYY format to YYYY-MM-DD for date input
    const convertDateForInput = (dateString: string) => {
      if (!dateString) return '';
      // Check if it's already in YYYY-MM-DD format
      if (dateString.includes('-') && dateString.length === 10) {
        return dateString;
      }
      // Convert from DD/MM/YYYY to YYYY-MM-DD
      if (dateString.includes('/')) {
        const parts = dateString.split('/');
        if (parts.length === 3) {
          const [day, month, year] = parts;
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }
      return dateString;
    };

    setEditClientForm({
      name: client.name || '',
      email: client.email || '',
      nationalID: client.nationalID || '',
      birthDate: convertDateForInput(client.birthDate || ''),
      mobileNumber: client.mobileNumber || '',
      mobileNumber2: client.mobileNumber2 || '',
      address: client.address || '',
      maritalStatus: client.maritalStatus || '',
      gender: client.gender || '',
      photoURL: client.photoURL || ''
    });
    setShowEditModal(true);
  };

  // Update client
  const updateClient = async () => {
    if (!selectedClient || !user) return;

    // Validate required fields
    if (!editClientForm.name.trim()) {
      setError('الاسم مطلوب');
      return;
    }
    if (!editClientForm.mobileNumber.trim()) {
      setError('رقم الهاتف المحمول مطلوب');
      return;
    }

    setEditingClient(true);
    setError(null);
    try {
      const currentTime = getCurrentMillis();
      
      // Format birth date to DD/MM/YYYY format
      const formatBirthDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };

      const clientRef = doc(db, 'Clients', selectedClient.id);
      const updatedData = {
        name: editClientForm.name.trim(),
        email: editClientForm.email.trim(),
        nationalID: editClientForm.nationalID.trim(),
        birthDate: formatBirthDate(editClientForm.birthDate),
        mobileNumber: editClientForm.mobileNumber.trim(),
        mobileNumber2: editClientForm.mobileNumber2.trim(),
        address: editClientForm.address.trim(),
        maritalStatus: editClientForm.maritalStatus,
        gender: editClientForm.gender,
        photoURL: editClientForm.photoURL.trim(),
        updatedAt: currentTime,
        updatedBy: user.id
      };

      await updateDoc(clientRef, updatedData);
      console.log('✅ Client updated:', selectedClient.id);

      // Update the selected client with new data
      setSelectedClient(prev => prev ? { ...prev, ...updatedData } : null);

      // Refresh the clients list
      if (selectedRole) {
        await fetchClientsForRole(selectedRole);
      }

      // Close edit modal
      setShowEditModal(false);
    } catch (error) {
      console.error('❌ Error updating client:', error);
      setError('Failed to update client');
    } finally {
      setEditingClient(false);
    }
  };

  // Custom Date Picker Component for New Client
  const CustomDatePicker = () => {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
    const months = language === 'ar' ? [
      { value: 1, label: 'يناير' },
      { value: 2, label: 'فبراير' },
      { value: 3, label: 'مارس' },
      { value: 4, label: 'أبريل' },
      { value: 5, label: 'مايو' },
      { value: 6, label: 'يونيو' },
      { value: 7, label: 'يوليو' },
      { value: 8, label: 'أغسطس' },
      { value: 9, label: 'سبتمبر' },
      { value: 10, label: 'أكتوبر' },
      { value: 11, label: 'نوفمبر' },
      { value: 12, label: 'ديسمبر' }
    ] : [
      { value: 1, label: 'January' },
      { value: 2, label: 'February' },
      { value: 3, label: 'March' },
      { value: 4, label: 'April' },
      { value: 5, label: 'May' },
      { value: 6, label: 'June' },
      { value: 7, label: 'July' },
      { value: 8, label: 'August' },
      { value: 9, label: 'September' },
      { value: 10, label: 'October' },
      { value: 11, label: 'November' },
      { value: 12, label: 'December' }
    ];

    const getDaysInMonth = (year: number, month: number) => {
      return new Date(year, month, 0).getDate();
    };

    // Initialize with current form value or default to 25 years ago
    const initializeDate = () => {
      if (newClientForm.birthDate) {
        const date = new Date(newClientForm.birthDate);
        return {
          year: date.getFullYear(),
          month: date.getMonth() + 1,
          day: date.getDate()
        };
      }
      return {
        year: new Date().getFullYear() - 25,
        month: 1,
        day: 1
      };
    };

    const initialDate = initializeDate();
    const [selectedYear, setSelectedYear] = useState(initialDate.year);
    const [selectedMonth, setSelectedMonth] = useState(initialDate.month);
    const [selectedDay, setSelectedDay] = useState(initialDate.day);

    const daysInSelectedMonth = getDaysInMonth(selectedYear, selectedMonth);
    const days = Array.from({ length: daysInSelectedMonth }, (_, i) => i + 1);

    // Adjust selected day if it doesn't exist in the selected month
    useEffect(() => {
      if (selectedDay > daysInSelectedMonth) {
        setSelectedDay(daysInSelectedMonth);
      }
    }, [selectedMonth, selectedYear, selectedDay, daysInSelectedMonth]);

    // Close date picker when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Element;
        if (showDatePicker && !target.closest('.date-picker-container')) {
          setShowDatePicker(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    const handleDateSelect = () => {
      const formattedDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
      handleFormChange('birthDate', formattedDate);
      setShowDatePicker(false);
    };

    const formatDisplayDate = (dateString: string) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    return (
      <div className="relative date-picker-container">
        <button
          type="button"
          onClick={() => setShowDatePicker(!showDatePicker)}
          className={`w-full px-3 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent cursor-pointer flex items-center justify-between ${
            isRTL ? 'pr-10 text-right' : 'pl-10 text-left'
          }`}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          <span className={newClientForm.birthDate ? 'text-slate-900 dark:text-white' : 'text-slate-500'}>
            {newClientForm.birthDate ? formatDisplayDate(newClientForm.birthDate) : strings.myClients.birthDatePlaceholder}
          </span>
          <ChevronDown className="w-4 h-4 text-slate-400" />
        </button>
        
        {showDatePicker && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg z-50 p-4">
            <div className="grid grid-cols-3 gap-2 mb-4">
              {/* Day */}
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  {language === 'ar' ? 'اليوم' : 'Day'}
                </label>
                <select
                  value={selectedDay}
                  onChange={(e) => setSelectedDay(Number(e.target.value))}
                  className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-1 focus:ring-primary"
                  dir={isRTL ? 'rtl' : 'ltr'}
                >
                  {days.map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </div>
              
              {/* Month */}
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  {language === 'ar' ? 'الشهر' : 'Month'}
                </label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-1 focus:ring-primary"
                  dir={isRTL ? 'rtl' : 'ltr'}
                >
                  {months.map(month => (
                    <option key={month.value} value={month.value}>{month.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Year */}
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  {language === 'ar' ? 'السنة' : 'Year'}
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-1 focus:ring-primary"
                  dir={isRTL ? 'rtl' : 'ltr'}
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className={`flex ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
              <button
                type="button"
                onClick={handleDateSelect}
                className="flex-1 px-3 py-2 bg-primary text-white text-sm rounded hover:bg-primary-dark transition-colors"
              >
                {strings.common.confirm}
              </button>
              <button
                type="button"
                onClick={() => setShowDatePicker(false)}
                className="px-3 py-2 bg-slate-500 text-white text-sm rounded hover:bg-slate-600 transition-colors"
              >
                {strings.common.cancel}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Custom Date Picker Component for Edit Client
  const EditCustomDatePicker = () => {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
    const months = language === 'ar' ? [
      { value: 1, label: 'يناير' },
      { value: 2, label: 'فبراير' },
      { value: 3, label: 'مارس' },
      { value: 4, label: 'أبريل' },
      { value: 5, label: 'مايو' },
      { value: 6, label: 'يونيو' },
      { value: 7, label: 'يوليو' },
      { value: 8, label: 'أغسطس' },
      { value: 9, label: 'سبتمبر' },
      { value: 10, label: 'أكتوبر' },
      { value: 11, label: 'نوفمبر' },
      { value: 12, label: 'ديسمبر' }
    ] : [
      { value: 1, label: 'January' },
      { value: 2, label: 'February' },
      { value: 3, label: 'March' },
      { value: 4, label: 'April' },
      { value: 5, label: 'May' },
      { value: 6, label: 'June' },
      { value: 7, label: 'July' },
      { value: 8, label: 'August' },
      { value: 9, label: 'September' },
      { value: 10, label: 'October' },
      { value: 11, label: 'November' },
      { value: 12, label: 'December' }
    ];

    const getDaysInMonth = (year: number, month: number) => {
      return new Date(year, month, 0).getDate();
    };

    // Initialize with current form value or default to 25 years ago
    const initializeDate = () => {
      if (editClientForm.birthDate) {
        const date = new Date(editClientForm.birthDate);
        return {
          year: date.getFullYear(),
          month: date.getMonth() + 1,
          day: date.getDate()
        };
      }
      return {
        year: new Date().getFullYear() - 25,
        month: 1,
        day: 1
      };
    };

    const initialDate = initializeDate();
    const [selectedYear, setSelectedYear] = useState(initialDate.year);
    const [selectedMonth, setSelectedMonth] = useState(initialDate.month);
    const [selectedDay, setSelectedDay] = useState(initialDate.day);

    const daysInSelectedMonth = getDaysInMonth(selectedYear, selectedMonth);
    const days = Array.from({ length: daysInSelectedMonth }, (_, i) => i + 1);

    // Adjust selected day if it doesn't exist in the selected month
    useEffect(() => {
      if (selectedDay > daysInSelectedMonth) {
        setSelectedDay(daysInSelectedMonth);
      }
    }, [selectedMonth, selectedYear, selectedDay, daysInSelectedMonth]);

    // Close date picker when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Element;
        if (showDatePicker && !target.closest('.edit-date-picker-container')) {
          setShowDatePicker(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    const handleDateSelect = () => {
      const formattedDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
      handleEditFormChange('birthDate', formattedDate);
      setShowDatePicker(false);
    };

    const formatDisplayDate = (dateString: string) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    return (
      <div className="relative edit-date-picker-container">
        <button
          type="button"
          onClick={() => setShowDatePicker(!showDatePicker)}
          className={`w-full px-3 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent cursor-pointer flex items-center justify-between ${
            isRTL ? 'pr-10 text-right' : 'pl-10 text-left'
          }`}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          <span className={editClientForm.birthDate ? 'text-slate-900 dark:text-white' : 'text-slate-500'}>
            {editClientForm.birthDate ? formatDisplayDate(editClientForm.birthDate) : strings.myClients.birthDatePlaceholder}
          </span>
          <ChevronDown className="w-4 h-4 text-slate-400" />
        </button>
        
        {showDatePicker && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg z-50 p-4">
            <div className="grid grid-cols-3 gap-2 mb-4">
              {/* Day */}
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  {language === 'ar' ? 'اليوم' : 'Day'}
                </label>
                <select
                  value={selectedDay}
                  onChange={(e) => setSelectedDay(Number(e.target.value))}
                  className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-1 focus:ring-primary"
                  dir={isRTL ? 'rtl' : 'ltr'}
                >
                  {days.map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </div>
              
              {/* Month */}
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  {language === 'ar' ? 'الشهر' : 'Month'}
                </label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-1 focus:ring-primary"
                  dir={isRTL ? 'rtl' : 'ltr'}
                >
                  {months.map(month => (
                    <option key={month.value} value={month.value}>{month.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Year */}
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  {language === 'ar' ? 'السنة' : 'Year'}
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-1 focus:ring-primary"
                  dir={isRTL ? 'rtl' : 'ltr'}
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className={`flex ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
              <button
                type="button"
                onClick={handleDateSelect}
                className="flex-1 px-3 py-2 bg-primary text-white text-sm rounded hover:bg-primary-dark transition-colors"
              >
                {strings.common.confirm}
              </button>
              <button
                type="button"
                onClick={() => setShowDatePicker(false)}
                className="px-3 py-2 bg-slate-500 text-white text-sm rounded hover:bg-slate-600 transition-colors"
              >
                {strings.common.cancel}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };


  if (loading || rolesLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">Loading Your Clients</h2>
          <p className="text-slate-600 dark:text-slate-300">Fetching clients based on your roles...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 dark:text-red-400 text-2xl">⚠️</span>
          </div>
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">{strings.common.error}</h2>
          <p className="text-slate-600 dark:text-slate-300 mb-6">{error}</p>
          <button
            onClick={handleRefresh}
            className="px-6 py-3 bg-primary text-white rounded-2xl hover:bg-primary-dark transition-colors duration-300"
          >
            {strings.common.refresh}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <SectionHeader
          title={strings.myClients.title}
          description={strings.myClients.selectRole}
          language={language}
          isRTL={isRTL}
          actionButtons={[
            {
              label: language === 'ar' ? 'تصدير إكسل' : 'Export Excel',
              onClick: exportToExcel,
              icon: <Download className="w-4 h-4" />,
              variant: 'secondary',
              requiresProfessional: false
            }
          ]}
          refreshButton={{
            onClick: handleRefresh,
            disabled: refreshing,
            loading: refreshing
          }}
          addButton={{
            label: language === 'ar' ? 'إضافة عميل' : 'Add Client',
            onClick: handleAddClientClick
          }}
        >
          {/* Roles Picker */}
          {roles.length > 0 && (
            <div className="mb-6">
              <label className={`block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
                {language === 'ar' ? 'اختر دور المكتب' : 'Select Office Role'}
              </label>
              <div className={`flex flex-wrap gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {roles.map((role) => (
                  <button
                    key={role.id}
                    onClick={() => handleRoleChange(role)}
                    className={`px-4 py-3 rounded-xl border-2 transition-all duration-300 flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'} ${
                      selectedRole?.id === role.id
                        ? 'border-primary bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 shadow-lg'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-md'
                    }`}
                  >
                    <Building2 className="w-5 h-5" />
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                      <div className="font-semibold">{role.officeName}</div>
                      <div className="text-sm opacity-75">{role.lawyerTitleID ? getLawyerTitleName(role.lawyerTitleID, language as 'en' | 'ar') : 'Legal Professional'}</div>
                    </div>
                    {selectedRole?.id === role.id && (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </SectionHeader>

        {/* Main Content - Two Panel Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Clients List */}
          <div className="lg:col-span-1 bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-slate-800 dark:text-white">
                  {language === 'ar' ? 'قائمة العملاء' : 'Clients List'}
                  {selectedRole && (
                    <span className="text-sm font-normal text-slate-500 dark:text-slate-400 mr-2">
                      ({filteredClients.length} {language === 'ar' ? 'موكل' : 'client'})
                    </span>
                  )}
                </h2>
              </div>
            </div>
            
            <div className="p-6">
              {!selectedRole ? (
                <div className="text-center py-8">
                  <Building2 className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-500 dark:text-slate-400">اختر دور لعرض العملاء</p>
                </div>
              ) : filteredClients.length === 0 ? (
                <div className="text-center py-8">
                  <UsersIcon className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-500 dark:text-slate-400">
                    {searchQuery ? 'لا توجد عملاء تطابق البحث' : 'لا توجد عملاء لهذا الدور'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {filteredClients.map((client) => (
                    <div
                      key={client.id}
                      onClick={() => handleClientSelect(client)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                        selectedClient?.id === client.id
                          ? 'border-primary bg-blue-50 dark:bg-blue-900/20 shadow-lg'
                          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary-dark rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                          {client.photoURL ? (
                            <img 
                              src={client.photoURL} 
                              alt={client.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            client.name.charAt(0).toUpperCase()
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-800 dark:text-white truncate">
                            {client.name}
                          </h3>
                          {client.mobileNumber && (
                            <p className="text-sm text-slate-600 dark:text-slate-400 flex items-center space-x-1">
                              <Phone className="w-3 h-3" />
                              <span>{client.mobileNumber}</span>
                            </p>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Client Details */}
          <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-slate-800 dark:text-white">
                  تفاصيل الموكل
                </h2>
                {selectedClient && (
                  <button
                    onClick={() => setSelectedClient(null)}
                    className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                    title="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            
            <div className="p-6">
              {!selectedClient ? (
                <div className="text-center py-8">
                  <User className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-500 dark:text-slate-400">اختر موكل لعرض التفاصيل</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Client Header */}
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-dark rounded-full flex items-center justify-center text-white font-semibold text-xl flex-shrink-0">
                      {selectedClient.photoURL ? (
                        <img 
                          src={selectedClient.photoURL} 
                          alt={selectedClient.name}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                      ) : (
                        selectedClient.name.charAt(0).toUpperCase()
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-bold text-slate-800 dark:text-white truncate">
                        {selectedClient.name}
                      </h3>
                      <p className="text-slate-600 dark:text-slate-400">
                        رقم الموكل: {selectedClient.id.slice(-8)}
                      </p>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-slate-800 dark:text-white">معلومات الاتصال</h4>
                    
                    <div className="grid grid-cols-1 gap-4">
                      {selectedClient.mobileNumber && (
                        <div className="flex items-center space-x-3 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                          <Phone className="w-5 h-5 text-primary flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-slate-600 dark:text-slate-400">رقم الهاتف المحمول</p>
                            <p className="text-slate-800 dark:text-white font-medium">{selectedClient.mobileNumber}</p>
                          </div>
                        </div>
                      )}

                      {selectedClient.mobileNumber2 && (
                        <div className="flex items-center space-x-3 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                          <Phone className="w-5 h-5 text-primary flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-slate-600 dark:text-slate-400">رقم هاتف إضافي</p>
                            <p className="text-slate-800 dark:text-white font-medium">{selectedClient.mobileNumber2}</p>
                          </div>
                        </div>
                      )}
                    
                      {selectedClient.email && (
                        <div className="flex items-center space-x-3 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                          <Mail className="w-5 h-5 text-primary flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-slate-600 dark:text-slate-400">البريد الإلكتروني</p>
                            <p className="text-slate-800 dark:text-white font-medium">{selectedClient.email}</p>
                          </div>
                        </div>
                      )}
                    
                      {selectedClient.address && (
                        <div className="flex items-start space-x-3 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                          <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-slate-600 dark:text-slate-400">العنوان</p>
                            <p className="text-slate-800 dark:text-white font-medium">{selectedClient.address}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-slate-800 dark:text-white">المعلومات الشخصية</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">النوع</p>
                        <p className="text-slate-800 dark:text-white font-medium">
                          {selectedClient.gender === '0' ? 'ذكر' : selectedClient.gender === '1' ? 'أنثى' : 'غير محدد'}
                        </p>
                      </div>
                      
                      <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">الحالة الاجتماعية</p>
                        <p className="text-slate-800 dark:text-white font-medium">
                          {selectedClient.maritalStatus === '0' ? 'أعزب' : 
                           selectedClient.maritalStatus === '1' ? 'متزوج' : 
                           selectedClient.maritalStatus === '2' ? 'مطلق' : 
                           selectedClient.maritalStatus === '3' ? 'أرمل/أرملة' : 'غير محدد'}
                        </p>
                      </div>
                      
                      {selectedClient.nationalID && (
                        <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                          <p className="text-sm font-medium text-slate-600 dark:text-slate-400">الرقم القومي</p>
                          <p className="text-slate-800 dark:text-white font-medium">{selectedClient.nationalID}</p>
                        </div>
                      )}

                      {selectedClient.birthDate && (
                        <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                          <p className="text-sm font-medium text-slate-600 dark:text-slate-400">تاريخ الميلاد</p>
                          <p className="text-slate-800 dark:text-white font-medium">{selectedClient.birthDate}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* System Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-slate-800 dark:text-white">معلومات النظام</h4>
                    
                    <div className="grid grid-cols-1 gap-4">
                      <div className="flex items-center space-x-3 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                        <Calendar className="w-5 h-5 text-primary" />
                        <div>
                          <p className="text-sm font-medium text-slate-600 dark:text-slate-400">تاريخ الإنشاء</p>
                          <p className="text-slate-800 dark:text-white font-medium">
                            {new Date(parseInt(selectedClient.createdAt)).toLocaleDateString('ar-EG', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                        <Calendar className="w-5 h-5 text-primary" />
                        <div>
                          <p className="text-sm font-medium text-slate-600 dark:text-slate-400">آخر تحديث</p>
                          <p className="text-slate-800 dark:text-white font-medium">
                            {new Date(parseInt(selectedClient.updatedAt)).toLocaleDateString('ar-EG', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-slate-600 dark:text-slate-400">حالة الموكل</p>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            selectedClient.isDeleted 
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                              : selectedClient.isArchived
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                              : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          }`}>
                            {selectedClient.isDeleted ? 'محذوف' : selectedClient.isArchived ? 'مؤرشف' : 'نشط'}
                          </span>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-slate-600 dark:text-slate-400">عدد جهات العمل المرتبطة</p>
                          <p className="text-slate-800 dark:text-white font-medium">
                            {selectedClient.rolesIDs ? selectedClient.rolesIDs.length : 0}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2 pt-4 border-t border-slate-200 dark:border-slate-700">
                    <button
                      onClick={() => openEditModal(selectedClient)}
                      className="flex items-center space-x-2 px-4 py-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                    >
                      <EditButton size="sm" onClick={() => {}} />
                      <span>تعديل الموكل</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Client Modal */}
      {showAddClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                  <button
                    onClick={() => {
                      setShowAddClient(false);
                      setPhoneSearch('');
                      setFoundClient(null);
                      setShowClientForm(false);
                      setClientAlreadyAssociated(false);
                      setError(null);
                      setHasSearched(false);
                    }}
                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    {isRTL ? (
                      <ChevronRight className="w-5 h-5 text-slate-500" />
                    ) : (
                      <ArrowRight className="w-5 h-5 text-slate-500" />
                    )}
                  </button>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                      {strings.myClients.addNewClientTitle}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {showClientForm ? strings.myClients.step2Title : strings.myClients.step1Title}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowAddClient(false);
                    setPhoneSearch('');
                    setFoundClient(null);
                    setShowClientForm(false);
                    setClientAlreadyAssociated(false);
                    setError(null);
                  }}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}
              {!showClientForm ? (
                // Phone Search Step
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {strings.myClients.searchByPhone}
                    </label>
                    <div className={`flex ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                      <div className="flex-1 relative">
                        <input
                          type="tel"
                          value={phoneSearch}
                          onChange={(e) => {
                            setPhoneSearch(e.target.value);
                            setHasSearched(false); // Reset search state when user types
                            setFoundClient(null);
                            setClientAlreadyAssociated(false);
                          }}
                          placeholder={strings.myClients.phonePlaceholder}
                          className={`w-full px-3 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                            isRTL ? 'pr-10 text-right' : 'pl-10 text-left'
                          }`}
                          dir={isRTL ? 'rtl' : 'ltr'}
                          onKeyPress={(e) => e.key === 'Enter' && handlePhoneSearch()}
                        />
                        <Phone className={`absolute top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 ${
                          isRTL ? 'right-3' : 'left-3'
                        }`} />
                      </div>
                      <button
                        onClick={handlePhoneSearch}
                        disabled={searchingClient || !phoneSearch.trim()}
                        className={`px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center ${
                          isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'
                        }`}
                      >
                        {searchingClient ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                        <span>{strings.myClients.searchButton}</span>
                      </button>
                    </div>
                  </div>

                  {foundClient ? (
                    // Client Found
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                      <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                        <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-green-800 dark:text-green-200">
                            {strings.myClients.clientFound}: {foundClient.name}
                          </h4>
                          <p className="text-sm text-green-600 dark:text-green-400">
                            {foundClient.mobileNumber} • {foundClient.email || strings.common.no}
                          </p>
                        </div>
                      </div>
                      <div className={`mt-4 flex ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                        <button
                          onClick={() => addExistingClientToOffice(foundClient)}
                          disabled={addingClient}
                          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {addingClient ? strings.myClients.adding : strings.myClients.addToOffice}
                        </button>
                        <button
                          onClick={() => {
                            setFoundClient(null);
                            setPhoneSearch('');
                            setHasSearched(false);
                          }}
                          className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                        >
                          {strings.common.cancel}
                        </button>
                      </div>
                    </div>
                  ) : clientAlreadyAssociated ? (
                    // Client Already Associated
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                      <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                        <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-red-600 dark:text-red-400" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-red-800 dark:text-red-200">
                            {strings.myClients.clientAlreadyAssociated}
                          </h4>
                          <p className="text-sm text-red-600 dark:text-red-400">
                            {strings.myClients.clientAlreadyAssociatedMessage}
                          </p>
                        </div>
                      </div>
                      <div className={`mt-4 flex ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                        <button
                          onClick={() => {
                            setPhoneSearch('');
                            setClientAlreadyAssociated(false);
                            setHasSearched(false);
                          }}
                          className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                        >
                          {strings.myClients.tryAnotherNumber}
                        </button>
                      </div>
                    </div>
                  ) : hasSearched && phoneSearch && !searchingClient && !foundClient && !clientAlreadyAssociated ? (
                    // No Client Found
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                      <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                        <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-yellow-800 dark:text-yellow-200">
                            {strings.myClients.noClientFound}
                          </h4>
                          <p className="text-sm text-yellow-600 dark:text-yellow-400">
                            {strings.myClients.noClientFoundMessage.replace('{phone}', phoneSearch)}
                          </p>
                        </div>
                      </div>
                      <div className={`mt-4 flex ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                        <button
                          onClick={() => {
                            setNewClientForm(prev => ({ ...prev, mobileNumber: phoneSearch }));
                            setShowClientForm(true);
                          }}
                          className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                        >
                          {strings.myClients.createNewClient}
                        </button>
                        <button
                          onClick={() => {
                            setPhoneSearch('');
                            setFoundClient(null);
                            setClientAlreadyAssociated(false);
                            setHasSearched(false);
                          }}
                          className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                        >
                          {strings.myClients.tryAgain}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                // Client Form Step
                <div className="space-y-6">
                  {/* Full Name */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {strings.myClients.fullName} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={newClientForm.name}
                        onChange={(e) => handleFormChange('name', e.target.value)}
                        className={`w-full px-3 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                          isRTL ? 'pr-10 text-right' : 'pl-10 text-left'
                        }`}
                        dir={isRTL ? 'rtl' : 'ltr'}
                        placeholder={strings.myClients.fullNamePlaceholder}
                        required
                      />
                      <User className={`absolute top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 ${
                        isRTL ? 'right-3' : 'left-3'
                      }`} />
                    </div>
                  </div>

                  {/* Mobile Number */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {strings.myClients.mobileNumber} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        value={newClientForm.mobileNumber}
                        onChange={(e) => handleFormChange('mobileNumber', e.target.value)}
                        className={`w-full px-3 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                          isRTL ? 'pr-10 text-right' : 'pl-10 text-left'
                        }`}
                        dir={isRTL ? 'rtl' : 'ltr'}
                        placeholder={strings.myClients.mobileNumberPlaceholder}
                        required
                      />
                      <Phone className={`absolute top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 ${
                        isRTL ? 'right-3' : 'left-3'
                      }`} />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {strings.myClients.emailLabel}
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        value={newClientForm.email}
                        onChange={(e) => handleFormChange('email', e.target.value)}
                        className={`w-full px-3 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                          isRTL ? 'pr-10 text-right' : 'pl-10 text-left'
                        }`}
                        dir={isRTL ? 'rtl' : 'ltr'}
                        placeholder={strings.myClients.emailPlaceholder}
                      />
                      <Mail className={`absolute top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 ${
                        isRTL ? 'right-3' : 'left-3'
                      }`} />
                    </div>
                  </div>

                  {/* Birth Date */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {strings.myClients.birthDate}
                    </label>
                    <div className="relative">
                      <CustomDatePicker />
                      <Calendar className={`absolute top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none ${
                        isRTL ? 'right-3' : 'left-3'
                      }`} />
                    </div>
                    {newClientForm.birthDate && (
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {strings.myClients.selectedDate}: {new Date(newClientForm.birthDate).toLocaleDateString(
                          language === 'ar' ? 'ar-EG' : 'en-US',
                          {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          }
                        )}
                      </p>
                    )}
                  </div>

                  {/* Address */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {strings.myClients.address}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={newClientForm.address}
                        onChange={(e) => handleFormChange('address', e.target.value)}
                        className={`w-full px-3 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                          isRTL ? 'pr-10 text-right' : 'pl-10 text-left'
                        }`}
                        dir={isRTL ? 'rtl' : 'ltr'}
                        placeholder={strings.myClients.address}
                      />
                      <MapPin className={`absolute top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 ${
                        isRTL ? 'right-3' : 'left-3'
                      }`} />
                    </div>
                  </div>

                  {/* Gender */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                      {strings.myClients.gender}
                    </label>
                    <div className={`flex ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
                      <label className={`flex items-center cursor-pointer ${
                        isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'
                      }`}>
                        <input
                          type="radio"
                          name="gender"
                          value="0"
                          checked={newClientForm.gender === '0'}
                          onChange={(e) => handleFormChange('gender', e.target.value)}
                          className="w-4 h-4 text-primary focus:ring-primary"
                        />
                        <span className="text-slate-700 dark:text-slate-300">{strings.myClients.male}</span>
                      </label>
                      <label className={`flex items-center cursor-pointer ${
                        isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'
                      }`}>
                        <input
                          type="radio"
                          name="gender"
                          value="1"
                          checked={newClientForm.gender === '1'}
                          onChange={(e) => handleFormChange('gender', e.target.value)}
                          className="w-4 h-4 text-primary focus:ring-primary"
                        />
                        <span className="text-slate-700 dark:text-slate-300">{strings.myClients.female}</span>
                      </label>
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="pt-4">
                    <button
                      onClick={createNewClient}
                      disabled={addingClient || !newClientForm.name.trim() || !newClientForm.mobileNumber.trim()}
                      className="w-full px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                    >
                      {addingClient ? strings.myClients.creating : strings.myClients.next}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Client Modal */}
      {showEditModal && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setError(null);
                    }}
                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    {isRTL ? (
                      <ChevronRight className="w-5 h-5 text-slate-500" />
                    ) : (
                      <ArrowRight className="w-5 h-5 text-slate-500" />
                    )}
                  </button>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                      {strings.myClients.editClient}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {strings.myClients.editClientTitle.replace('{name}', selectedClient.name)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setError(null);
                  }}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              <div className="space-y-6">
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {strings.myClients.fullName} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={editClientForm.name}
                      onChange={(e) => handleEditFormChange('name', e.target.value)}
                      className={`w-full px-3 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                        isRTL ? 'pr-10 text-right' : 'pl-10 text-left'
                      }`}
                      dir={isRTL ? 'rtl' : 'ltr'}
                      placeholder={strings.myClients.fullNamePlaceholder}
                      required
                    />
                    <User className={`absolute top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 ${
                        isRTL ? 'right-3' : 'left-3'
                      }`} />
                  </div>
                </div>

                {/* Mobile Number */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {strings.myClients.mobileNumber} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      value={editClientForm.mobileNumber}
                      onChange={(e) => handleEditFormChange('mobileNumber', e.target.value)}
                      className={`w-full px-3 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                        isRTL ? 'pr-10 text-right' : 'pl-10 text-left'
                      }`}
                      dir={isRTL ? 'rtl' : 'ltr'}
                      placeholder={strings.myClients.mobileNumberPlaceholder}
                      required
                    />
                    <Phone className={`absolute top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 ${
                        isRTL ? 'right-3' : 'left-3'
                      }`} />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {strings.myClients.emailLabel}
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      value={editClientForm.email}
                      onChange={(e) => handleEditFormChange('email', e.target.value)}
                      className={`w-full px-3 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                        isRTL ? 'pr-10 text-right' : 'pl-10 text-left'
                      }`}
                      dir={isRTL ? 'rtl' : 'ltr'}
                      placeholder={strings.myClients.emailPlaceholder}
                    />
                    <Mail className={`absolute top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 ${
                        isRTL ? 'right-3' : 'left-3'
                      }`} />
                  </div>
                </div>

                {/* Birth Date */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {strings.myClients.birthDate}
                  </label>
                  <div className="relative">
                    <EditCustomDatePicker />
                    <Calendar className={`absolute top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none ${
                        isRTL ? 'right-3' : 'left-3'
                      }`} />
                  </div>
                  {editClientForm.birthDate && (
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {strings.myClients.selectedDate}: {new Date(editClientForm.birthDate).toLocaleDateString(
                        language === 'ar' ? 'ar-EG' : 'en-US',
                        {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        }
                      )}
                    </p>
                  )}
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {strings.myClients.address}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={editClientForm.address}
                      onChange={(e) => handleEditFormChange('address', e.target.value)}
                      className={`w-full px-3 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                        isRTL ? 'pr-10 text-right' : 'pl-10 text-left'
                      }`}
                      dir={isRTL ? 'rtl' : 'ltr'}
                      placeholder={strings.myClients.address}
                    />
                    <MapPin className={`absolute top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 ${
                        isRTL ? 'right-3' : 'left-3'
                      }`} />
                  </div>
                </div>

                {/* Gender */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                    {strings.myClients.gender}
                  </label>
                  <div className={`flex ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
                    <label className={`flex items-center cursor-pointer ${
                        isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'
                      }`}>
                      <input
                        type="radio"
                        name="editGender"
                        value="0"
                        checked={editClientForm.gender === '0'}
                        onChange={(e) => handleEditFormChange('gender', e.target.value)}
                        className="w-4 h-4 text-primary focus:ring-primary"
                      />
                      <span className="text-slate-700 dark:text-slate-300">{strings.myClients.male}</span>
                    </label>
                    <label className={`flex items-center cursor-pointer ${
                        isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'
                      }`}>
                      <input
                        type="radio"
                        name="editGender"
                        value="1"
                        checked={editClientForm.gender === '1'}
                        onChange={(e) => handleEditFormChange('gender', e.target.value)}
                        className="w-4 h-4 text-primary focus:ring-primary"
                      />
                      <span className="text-slate-700 dark:text-slate-300">{strings.myClients.female}</span>
                    </label>
                  </div>
                </div>

                {/* Marital Status */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {strings.myClients.maritalStatus}
                  </label>
                  <select
                    value={editClientForm.maritalStatus}
                    onChange={(e) => handleEditFormChange('maritalStatus', e.target.value)}
                    className={`w-full px-3 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                      isRTL ? 'text-right' : 'text-left'
                    }`}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  >
                    <option value="">{strings.myClients.selectMaritalStatus}</option>
                    <option value="0">{strings.myClients.single}</option>
                    <option value="1">{strings.myClients.married}</option>
                    <option value="2">{strings.myClients.divorced}</option>
                    <option value="3">{strings.myClients.widowed}</option>
                  </select>
                </div>

                {/* National ID */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {strings.myClients.nationalID}
                  </label>
                  <input
                    type="text"
                    value={editClientForm.nationalID}
                    onChange={(e) => handleEditFormChange('nationalID', e.target.value)}
                    className={`w-full px-3 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                      isRTL ? 'text-right' : 'text-left'
                    }`}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    placeholder={strings.myClients.nationalIDPlaceholder}
                  />
                </div>

                {/* Secondary Phone */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {strings.myClients.secondaryPhone}
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      value={editClientForm.mobileNumber2}
                      onChange={(e) => handleEditFormChange('mobileNumber2', e.target.value)}
                      className={`w-full px-3 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent ${
                        isRTL ? 'pr-10 text-right' : 'pl-10 text-left'
                      }`}
                      dir={isRTL ? 'rtl' : 'ltr'}
                      placeholder={strings.myClients.secondaryPhonePlaceholder}
                    />
                    <Phone className={`absolute top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 ${
                        isRTL ? 'right-3' : 'left-3'
                      }`} />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className={`flex pt-4 ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                  <button
                    onClick={updateClient}
                    disabled={editingClient || !editClientForm.name.trim() || !editClientForm.mobileNumber.trim()}
                    className={`flex-1 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center ${
                      isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'
                    }`}
                  >
                    {editingClient ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>{strings.myClients.updating}</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>{strings.myClients.saveChanges}</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="px-6 py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors font-medium"
                  >
                    إلغاء
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyClients;